/**
 * New York State-Specific Diagnostic Questions
 * CORRECTED VERSION - All citations verified against actual 10 NYCRR Part 766
 * 
 * Regulatory Body: NYS DOH (Licensed Home Care Services Agencies)
 * Primary Regulation: 10 NYCRR Part 766 - Licensed Home Care Services Agencies-Minimum Standards
 * Licensing Authority: Part 765 - Approval of Home Care Services Agencies
 * 
 * ACTUAL 10 NYCRR Part 766 Structure (Verified):
 * - § 766.1 - Patient rights
 * - § 766.2 - Patient service policies and procedures
 * - § 766.3 - Plan of care
 * - § 766.4 - Medical Orders (NOT policies!)
 * - § 766.5 - Clinical supervision
 * - § 766.6 - Patient care record
 * - § 766.7 - [RESERVED - DOES NOT EXIST]
 * - § 766.8 - [RESERVED - DOES NOT EXIST]
 * - § 766.9 - Governing authority (contains most policy requirements)
 *   - 766.9(c) - Emergency preparedness
 *   - 766.9(d) - Written policies
 *   - 766.9(h) - RN for direction/supervision
 *   - 766.9(j) - Patient complaint procedure
 *   - 766.9(k) - Quality improvement
 *   - 766.9(l) - Quality improvement committee
 *   - 766.9(o) - HPN requirements
 * - § 766.10 - Contracts
 * - § 766.11 - Personnel
 * - § 766.12 - Records and reports
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const NEWYORK_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'NY-A1',
    itemId: 'A1',
    stateCode: 'NY',
    title: 'LHCSA License Status',
    description: 'New York requires all home care agencies to be licensed by DOH under Public Health Law Article 36',
    severity: 'high',
    regulationCode: '10 NYCRR 765',
    regulationName: 'LHCSA License',
    agencyName: 'New York State Department of Health',
    agencyAbbr: 'NYS DOH',
    agencyPhone: '1-800-628-5972',
    agencyUrl: 'https://www.health.ny.gov/facilities/home_care',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Licensed Home Care Services Agency (LHCSA) license from NYS DOH?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'LHCSA licenses must be renewed every 4 years. Check your license status with NYS DOH.',
      },
      {
        id: 'q2',
        question: 'What is your LHCSA license expiration date?',
        inputType: 'date',
        helpText: 'Found on your LHCSA license certificate',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 180, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your NYS DOH Licensed Home Care Services Agency license is current and active per Public Health Law Article 36.',
      atRisk: 'Your LHCSA license expires within 180 days. New York requires renewal applications to be submitted to DOH 120 days before expiration. Submit your renewal application immediately.',
      nonCompliant: 'Operating a home care agency in New York without a valid LHCSA license violates Public Health Law Article 36 and can result in civil penalties and criminal prosecution. Contact NYS DOH immediately at 1-800-628-5972.',
      unknown: 'Verify your LHCSA license status with NYS DOH at 1-800-628-5972.',
    },
    stateNotes: [
      'LHCSA licenses are valid for 4 years',
      'Renewal applications must be submitted 120 days before expiration',
      'Agencies must maintain a physical office in New York',
      'Public need and financial feasibility review required since April 2020',
    ],
  },

  {
    id: 'NY-A2',
    itemId: 'A2',
    stateCode: 'NY',
    title: 'Governing Authority Responsibilities',
    description: 'Governing authority requirements per 10 NYCRR 766.9',
    severity: 'high',
    regulationCode: '10 NYCRR 766.9(a)',
    regulationName: 'Governing Body',
    agencyName: 'New York State Department of Health',
    agencyAbbr: 'NYS DOH',
    agencyPhone: '1-800-628-5972',
    agencyUrl: 'https://www.health.ny.gov/facilities/home_care',
    questions: [
      {
        id: 'q1',
        question: 'Does your governing authority ensure compliance with all applicable Federal, State and local statutes per 10 NYCRR 766.9(b)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is the governing authority responsible for the management and operation of the agency per 10 NYCRR 766.9(a)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Governing authority meets 10 NYCRR 766.9(a)-(b) requirements.',
      atRisk: 'Review governing authority responsibilities under 10 NYCRR 766.9.',
      nonCompliant: 'Governing authority responsibilities are not being met per 10 NYCRR 766.9.',
      unknown: 'Verify governing authority compliance with 10 NYCRR 766.9.',
    },
    stateNotes: [
      'Governing authority must ensure compliance with all Federal, State, and local statutes per 10 NYCRR 766.9(b)',
      'Must be responsible for management and operation of the agency per 10 NYCRR 766.9(a)',
      'Governing authority adopts and approves amendments to written policies per 10 NYCRR 766.9(d)',
      'Must maintain adequate financial resources and qualified personnel per Public Health Law Article 36',
    ],
  },

  {
    id: 'NY-A3',
    itemId: 'A3',
    stateCode: 'NY',
    title: 'Written Policies and Procedures',
    description: 'Written policies required per 10 NYCRR 766.9(d) and 766.2',
    severity: 'medium',
    regulationCode: '10 NYCRR 766.9(d)',
    regulationName: 'Policies',
    agencyName: 'New York State Department of Health',
    agencyAbbr: 'NYS DOH',
    agencyPhone: '1-800-628-5972',
    agencyUrl: 'https://www.health.ny.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your governing authority adopt and approve amendments to written policies per 10 NYCRR 766.9(d)?',
        inputType: 'select',
        options: [
          { value: 'complete', label: 'Yes, complete and updated within past year' },
          { value: 'partial', label: 'Partial manual, needs updating' },
          { value: 'outdated', label: 'Manual is outdated (over 2 years)' },
          { value: 'no', label: 'No formal manual' },
        ],
        required: true,
        helpText: 'Policy manual should be reviewed and updated annually',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'complete', result: 'COMPLIANT' },
      { condition: 'equals', value: 'partial', result: 'AT_RISK' },
      { condition: 'includes', value: ['outdated', 'no'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policies and procedures manual is complete and current per 10 NYCRR 766.9(d).',
      atRisk: 'Your policies and procedures manual needs updating. Review and update all sections annually.',
      nonCompliant: 'A complete policies and procedures manual is required per 10 NYCRR 766.9(d). Develop or update your manual immediately.',
      unknown: 'Review your policies and procedures manual against 10 NYCRR 766.9(d) requirements.',
    },
    stateNotes: [
      'Governing authority must adopt and approve amendments to written policies per 10 NYCRR 766.9(d)',
      'Policies must cover patient service requirements per 10 NYCRR 766.2',
      'Annual review recommended to ensure currency with regulatory changes',
      'DOH surveyors review policy manuals during inspections for completeness',
    ],
  },

  {
    id: 'NY-A4',
    itemId: 'A4',
    stateCode: 'NY',
    title: 'Required Agency Policies',
    description: 'New York requires specific written policies per 10 NYCRR 766.9 and 766.2',
    severity: 'medium',
    regulationCode: '10 NYCRR 766.9',
    regulationName: 'Agency Policies',
    agencyName: 'New York State Department of Health',
    agencyAbbr: 'NYS DOH',
    agencyPhone: '1-800-628-5972',
    agencyUrl: 'https://www.health.ny.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following New York-required policies does your agency have documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'patient_rights', label: 'Patient rights (10 NYCRR 766.1)' },
          { value: 'service_policies', label: 'Patient service policies (10 NYCRR 766.2)' },
          { value: 'plan_of_care', label: 'Plan of care procedures (10 NYCRR 766.3)' },
          { value: 'clinical_supervision', label: 'Clinical supervision (10 NYCRR 766.5)' },
          { value: 'complaint', label: 'Complaint procedures (10 NYCRR 766.9(j))' },
          { value: 'emergency', label: 'Emergency preparedness (10 NYCRR 766.9(c))' },
          { value: 'quality', label: 'Quality improvement (10 NYCRR 766.9(k))' },
          { value: 'personnel', label: 'Personnel policies (10 NYCRR 766.11)' },
          { value: 'records', label: 'Records and reports (10 NYCRR 766.12)' },
          { value: 'hpn', label: 'Health Provider Network (10 NYCRR 766.9(o))' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policy manual meets New York 10 NYCRR Part 766 requirements.',
      atRisk: 'Your policy manual is missing required sections per 10 NYCRR Part 766. NYS DOH surveyors will review your policies for completeness.',
      nonCompliant: 'Multiple required policies are missing per 10 NYCRR Part 766. This is a common DOH survey deficiency.',
      unknown: 'Review your policies against 10 NYCRR Part 766 requirements.',
    },
    stateNotes: [
      'Patient rights policy required per 10 NYCRR 766.1 - must be provided to all patients',
      'Plan of care procedures must be documented per 10 NYCRR 766.3',
      'Clinical supervision policies required per 10 NYCRR 766.5',
      'HPN requirements for 24/7 emergency contacts per 10 NYCRR 766.9(o)',
      'Quality improvement policies and committee required per 10 NYCRR 766.9(k)-(l)',
    ],
  },

  {
    id: 'NY-A5',
    itemId: 'A5',
    stateCode: 'NY',
    title: 'Emergency Preparedness Plan',
    description: 'Written emergency plan required per 10 NYCRR 766.9(c)',
    severity: 'high',
    regulationCode: '10 NYCRR 766.9(c)',
    regulationName: 'Emergency',
    agencyName: 'New York State Department of Health',
    agencyAbbr: 'NYS DOH',
    agencyPhone: '1-800-628-5972',
    agencyUrl: 'https://www.health.ny.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a written emergency plan per 10 NYCRR 766.9(c)?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes, comprehensive written plan in place' },
          { value: 'partial', label: 'Partial plan, needs updating' },
          { value: 'no', label: 'No emergency preparedness plan' },
        ],
        required: true,
        helpText: '10 NYCRR 766.9(c) requires procedures to ensure patient health care needs continue in emergencies',
      },
      {
        id: 'q2',
        question: 'Does your emergency plan include the following? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'continuity', label: 'Procedures to continue meeting patient health care needs' },
          { value: 'employee_orientation', label: 'Employee orientation to emergency responsibilities' },
          { value: 'patient_contact', label: 'Patient emergency contact procedures' },
          { value: 'staff_notification', label: 'Staff notification procedures' },
          { value: 'evacuation', label: 'Evacuation assistance for patients' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'documented' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'partial' },
        { condition: 'countAtLeast', questionId: 'q2', value: 2 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness plan is comprehensive and documented per 10 NYCRR 766.9(c).',
      atRisk: 'Emergency preparedness plan is incomplete. 10 NYCRR 766.9(c) requires procedures to ensure patient care continues during emergencies.',
      nonCompliant: 'No emergency preparedness plan is in place. This violates 10 NYCRR 766.9(c). Develop a comprehensive plan immediately.',
      unknown: 'Review your emergency preparedness procedures for completeness per 10 NYCRR 766.9(c).',
    },
    stateNotes: [
      'Written emergency plan required per 10 NYCRR 766.9(c) to ensure patient care continues',
      'Must include employee orientation to emergency responsibilities',
      'Plan must address natural disasters, power outages, and staffing emergencies',
      'Coordinate with local emergency management agencies as required by Public Health Law',
    ],
  },

  {
    id: 'NY-A6',
    itemId: 'A6',
    stateCode: 'NY',
    title: 'Patient Complaint Procedure',
    description: 'Patient complaint procedure required per 10 NYCRR 766.9(j)',
    severity: 'medium',
    regulationCode: '10 NYCRR 766.9(j)',
    regulationName: 'Complaints',
    agencyName: 'New York State Department of Health',
    agencyAbbr: 'NYS DOH',
    agencyPhone: '1-800-628-5972',
    agencyUrl: 'https://www.health.ny.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented patient complaint procedure per 10 NYCRR 766.9(j)?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes, documented process with tracking' },
          { value: 'informal', label: 'Informal process, not fully documented' },
          { value: 'no', label: 'No complaint handling process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your complaint process include the following required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'log', label: 'Complaint log with dates of receipt and resolution (766.9(j)(1))' },
          { value: 'written_response', label: 'Written response within 15 days (766.9(j)(2))' },
          { value: 'appeals', label: 'Appeals process with governing authority review within 30 days (766.9(j)(3))' },
          { value: 'doh_info', label: 'Notification of right to complain to DOH (766.9(j)(4))' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'documented' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'informal' },
        { condition: 'countAtLeast', questionId: 'q2', value: 2 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Complaint procedure is documented and meets 10 NYCRR 766.9(j) requirements.',
      atRisk: 'Complaint procedure may be missing required elements. Review 10 NYCRR 766.9(j)(1)-(4) for all requirements.',
      nonCompliant: 'No complaint handling process is in place. 10 NYCRR 766.9(j) requires written complaint procedures.',
      unknown: 'Verify your complaint procedures meet 10 NYCRR 766.9(j) requirements.',
    },
    stateNotes: [
      'Must maintain complaint log with dates of receipt and resolution per 10 NYCRR 766.9(j)(1)',
      'Written response required within 15 days per 10 NYCRR 766.9(j)(2)',
      'Appeals must go to governing authority within 30 days per 10 NYCRR 766.9(j)(3)',
      'Must notify patients of right to complain to DOH per 10 NYCRR 766.9(j)(4)',
      'DOH complaint hotline: 1-800-628-5972',
    ],
  },

  {
    id: 'NY-A7',
    itemId: 'A7',
    stateCode: 'NY',
    title: 'Quality Improvement Program',
    description: 'Quality improvement required per 10 NYCRR 766.9(k)-(l)',
    severity: 'medium',
    regulationCode: '10 NYCRR 766.9(k)',
    regulationName: 'QI Program',
    agencyName: 'New York State Department of Health',
    agencyAbbr: 'NYS DOH',
    agencyPhone: '1-800-628-5972',
    agencyUrl: 'https://www.health.ny.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a quality assessment and improvement program per 10 NYCRR 766.9(k)?',
        inputType: 'select',
        options: [
          { value: 'regular', label: 'Yes, coordinated program integrating all services' },
          { value: 'occasional', label: 'Occasional reviews, not coordinated' },
          { value: 'no', label: 'No quality improvement program' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you have a quality improvement committee per 10 NYCRR 766.9(l)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, committee meets at least quarterly' },
          { value: 'no', label: 'No committee or meets less frequently' },
        ],
        helpText: '10 NYCRR 766.9(l) requires a committee with consumer and health professional members meeting at least 4 times per year',
      },
      {
        id: 'q3',
        question: 'Does your quality committee perform the following? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'policy_review', label: 'Review policies and recommend changes (766.9(l)(1))' },
          { value: 'record_review', label: 'Conduct clinical record reviews (766.9(l)(2))' },
          { value: 'summary', label: 'Submit written summaries to governing authority (766.9(l)(3))' },
          { value: 'liaison', label: 'Maintain liaison with other providers (766.9(l)(4))' },
        ],
        showCondition: {
          questionId: 'q2',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'regular' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q3', value: 3 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'occasional' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Quality improvement program meets 10 NYCRR 766.9(k)-(l) requirements.',
      atRisk: 'Quality improvement program may be incomplete. Review 10 NYCRR 766.9(k)-(l) for all requirements.',
      nonCompliant: 'No quality improvement program exists. This violates 10 NYCRR 766.9(k). Implement a coordinated program immediately.',
      unknown: 'Review quality improvement requirements per 10 NYCRR 766.9(k)-(l).',
    },
    stateNotes: [
      'Coordinated QI program integrating all agency services required per 10 NYCRR 766.9(k)',
      'QI committee must meet at least quarterly per 10 NYCRR 766.9(l)',
      'Committee must include consumer and health professional members',
      'Must conduct clinical record reviews per 10 NYCRR 766.9(l)(2)',
      'Written summaries must be submitted to governing authority per 10 NYCRR 766.9(l)(3)',
    ],
  },

  {
    id: 'NY-A8',
    itemId: 'A8',
    stateCode: 'NY',
    title: 'Health Provider Network (HPN) Compliance',
    description: 'HPN account requirements per 10 NYCRR 766.9(o)',
    severity: 'medium',
    regulationCode: '10 NYCRR 766.9(o)',
    regulationName: 'HPN',
    agencyName: 'New York State Department of Health',
    agencyAbbr: 'NYS DOH',
    agencyPhone: '1-800-628-5972',
    agencyUrl: 'https://www.health.ny.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain current HPN accounts per 10 NYCRR 766.9(o)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Requires 24/7 emergency contacts and monthly updates to Communications Directory',
      },
      {
        id: 'q2',
        question: 'Do you have a policy defining HPN coverage reviewed at least annually?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HPN account maintenance meets 10 NYCRR 766.9(o) requirements.',
      atRisk: 'HPN policy may not be current. Review annually per 10 NYCRR 766.9(o).',
      nonCompliant: 'HPN accounts are not being maintained per 10 NYCRR 766.9(o).',
      unknown: 'Verify HPN compliance per 10 NYCRR 766.9(o).',
    },
    stateNotes: [
      'HPN accounts required for 24/7 emergency contacts per 10 NYCRR 766.9(o)',
      'Must update Communications Directory monthly',
      'Policy defining HPN coverage must be reviewed at least annually',
      'HPN enables communication with hospitals and other providers during emergencies',
    ],
  },

  {
    id: 'NY-A9',
    itemId: 'A9',
    stateCode: 'NY',
    title: 'Records and Reports Requirements',
    description: 'Record retention requirements per 10 NYCRR 766.12',
    severity: 'medium',
    regulationCode: '10 NYCRR 766.12',
    regulationName: 'Retention',
    agencyName: 'New York State Department of Health',
    agencyAbbr: 'NYS DOH',
    agencyPhone: '1-800-628-5972',
    agencyUrl: 'https://www.health.ny.gov',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain patient and employee records per 10 NYCRR 766.12?',
        inputType: 'select',
        options: [
          { value: '6plus', label: '6 years or longer (patient records)' },
          { value: '3plus', label: '3 years (personnel records after termination)' },
          { value: 'less', label: 'Less than required periods' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: '766.12 requires: patient records 6 years from discharge; personnel records 3 years from termination',
      },
    ],
    scoringRules: [
      { condition: 'includes', value: ['6plus', '3plus'], result: 'COMPLIANT' },
      { condition: 'equals', value: 'less', result: 'AT_RISK' },
      { condition: 'equals', value: 'unsure', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your record retention policy meets 10 NYCRR 766.12 requirements.',
      atRisk: 'Record retention may not meet 10 NYCRR 766.12 requirements. Patient records: 6 years; Personnel: 3 years after termination.',
      nonCompliant: 'No formal record retention policy exists. This violates 10 NYCRR 766.12.',
      unknown: 'Review record retention requirements under 10 NYCRR 766.12.',
    },
    stateNotes: [
      'Patient records must be retained 6 years from discharge per 10 NYCRR 766.12',
      'Personnel records must be retained 3 years after termination per 10 NYCRR 766.12',
      'Records must be maintained in a secure and accessible location',
      'Electronic records must meet DOH standards for security and retrieval',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL
  // ============================================
  {
    id: 'NY-B1',
    itemId: 'B1',
    stateCode: 'NY',
    title: 'Personnel Requirements (10 NYCRR 766.11)',
    description: 'Personnel record and qualification requirements',
    severity: 'high',
    regulationCode: '10 NYCRR 766.11',
    regulationName: 'Personnel',
    agencyName: 'New York State Department of Health',
    agencyAbbr: 'NYS DOH',
    agencyPhone: '1-800-628-5972',
    agencyUrl: 'https://www.health.ny.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following documents do you maintain in EVERY Home Health Aide\'s personnel file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'app', label: 'Employment application' },
          { value: 'id', label: 'Government-issued photo ID' },
          { value: 'hha_cert', label: 'Home Health Aide Certificate (from Home Care Registry)' },
          { value: 'competency', label: 'Competency evaluation documentation' },
          { value: 'background', label: 'Criminal history record check results' },
          { value: 'references', label: 'Employment history verification' },
          { value: 'medical', label: 'Physical exam/health assessment' },
          { value: 'tb', label: 'TB screening results' },
          { value: 'i9', label: 'I-9 Employment Eligibility Verification' },
          { value: 'training', label: 'Training documentation (75 hours initial)' },
        ],
        required: true,
        helpText: '10 NYCRR 766.11 requires verification of employment history and qualifications',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain required documentation per 10 NYCRR 766.11.',
      atRisk: 'Some personnel files may be missing required documents per 10 NYCRR 766.11.',
      nonCompliant: 'Personnel files are missing critical documentation required by 10 NYCRR 766.11.',
      unknown: 'Audit personnel files against 10 NYCRR 766.11 requirements.',
    },
    stateNotes: [
      'Must verify employment history and qualifications per 10 NYCRR 766.11',
      'HHA Certificate must be verified through Home Care Registry',
      'TB screening required before patient contact',
      'I-9 Employment Eligibility Verification required by federal law',
      '75 hours initial training documentation required for HHAs',
    ],
  },

  {
    id: 'NY-B2',
    itemId: 'B2',
    stateCode: 'NY',
    title: 'Criminal History Record Check',
    description: 'Criminal background check requirements per 10 NYCRR 766.11',
    severity: 'high',
    regulationCode: '10 NYCRR 766.11',
    regulationName: 'Background Checks',
    agencyName: 'New York State Department of Health',
    agencyAbbr: 'NYS DOH',
    agencyPhone: '1-800-628-5972',
    agencyUrl: 'https://www.health.ny.gov',
    questions: [
      {
        id: 'q1',
        question: 'For ALL currently employed staff with patient contact, which of the following do you have on file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'chrc', label: 'Criminal History Record Check (CHRC) results' },
          { value: 'dcjs', label: 'DCJS fingerprint clearance' },
          { value: 'fbi', label: 'FBI background check (if required)' },
          { value: 'exclusion', label: 'Exclusion list verification (OIG, Medicaid)' },
          { value: 'registry', label: 'Home Care Registry verification' },
        ],
        required: true,
        helpText: 'Per Chapter 594 of the Laws of 2008 and 10 NYCRR 766.11',
      },
      {
        id: 'q2',
        question: 'Do any employees begin working with patients BEFORE their background check is completed?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never before clearance' },
          { value: 'yes', label: 'Yes - sometimes they start early' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if staff start before clearance
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if missing required checks
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
      // COMPLIANT only if 4+ checks AND never start before clearance
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'COMPLIANT' },
      // AT_RISK if 3+ checks but not all required
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Background check documentation is complete for all staff per 10 NYCRR 766.11.',
      atRisk: 'Some required background checks may be missing. Ensure all CHRC requirements are met.',
      nonCompliant: 'VIOLATION: Staff are working before background clearance OR required checks are missing. This violates 10 NYCRR 766.11.',
      unknown: 'Audit all personnel files for background check documentation per 10 NYCRR 766.11.',
    },
    stateNotes: [
      'Criminal History Record Check (CHRC) required per Chapter 594 of the Laws of 2008',
      'DCJS fingerprint clearance required for all direct care staff',
      'FBI background check may be required for certain positions',
      'OIG and Medicaid exclusion list verification required monthly',
      'Staff cannot work before background clearance is received',
    ],
  },

  {
    id: 'NY-B3',
    itemId: 'B3',
    stateCode: 'NY',
    title: 'Home Care Registry Verification',
    description: 'HHA certificates must be verified through Home Care Registry',
    severity: 'high',
    regulationCode: '10 NYCRR 766.11',
    regulationName: 'Registry',
    agencyName: 'New York State Department of Health',
    agencyAbbr: 'NYS DOH',
    agencyPhone: '1-800-628-5972',
    agencyUrl: 'https://www.health.ny.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify HHA/PCA certificate validity through the Home Care Registry before hiring?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, 100% are verified via Home Care Registry' },
          { value: 'most', label: 'Most are verified' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
        helpText: 'Per DAL DHCBS 22-07, Home Care Registry verification is required for all aides',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All aides are verified through the Home Care Registry per 10 NYCRR 766.11.',
      atRisk: 'Registry verification is inconsistent. Verify all aides via Home Care Registry before hiring.',
      nonCompliant: 'Failure to verify registry status before hiring violates 10 NYCRR 766.11.',
      unknown: 'Verify all staff through the Home Care Registry.',
    },
    stateNotes: [
      'HHA certificates for aides trained after September 25, 2009 are generated by Home Care Registry',
      'PCA and HHA certificates do not have expiration dates',
      'Annual in-service requirements are a condition of employment, not certificate status',
    ],
  },

  {
    id: 'NY-B4',
    itemId: 'B4',
    stateCode: 'NY',
    title: 'RN Director/Supervisor',
    description: 'RN required for direction and supervision per 10 NYCRR 766.9(h)',
    severity: 'high',
    regulationCode: '10 NYCRR 766.9(h)',
    regulationName: 'RN Supervision',
    agencyName: 'New York State Department of Health',
    agencyAbbr: 'NYS DOH',
    agencyPhone: '1-800-628-5972',
    agencyUrl: 'https://www.health.ny.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you employ at least one licensed RN responsible for direction and supervision of all patient care services per 10 NYCRR 766.9(h)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is this RN\'s license current and verified?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, verified at op.nysed.gov' },
          { value: 'no', label: 'No / not verified' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'RN supervision meets 10 NYCRR 766.9(h) requirements.',
      atRisk: 'RN license should be verified at op.nysed.gov.',
      nonCompliant: 'No RN employed for direction and supervision. This violates 10 NYCRR 766.9(h).',
      unknown: 'Verify RN employment and licensure per 10 NYCRR 766.9(h).',
    },
    stateNotes: [
      'At least one RN required for direction and supervision per 10 NYCRR 766.9(h)',
      'RN license must be verified at op.nysed.gov',
      'RN is responsible for clinical supervision per 10 NYCRR 766.5',
      'RN must be available for patient care oversight during operating hours',
    ],
  },

  {
    id: 'NY-B5',
    itemId: 'B5',
    stateCode: 'NY',
    title: 'OIG/Medicaid Exclusion List Verification',
    description: 'Must verify staff are not on federal exclusion lists',
    severity: 'high',
    regulationCode: '42 CFR 1001',
    regulationName: 'OIG Exclusions',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the OIG exclusion list for all employees before hire?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all employees are checked before hire' },
          { value: 'most', label: 'Most are checked' },
          { value: 'no', label: 'No / not aware of this requirement' },
        ],
        required: true,
        helpText: 'Check at exclusions.oig.hhs.gov',
      },
      {
        id: 'q2',
        question: 'Do you perform monthly exclusion list checks for all current employees?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, monthly checks' },
          { value: 'no', label: 'No / less frequently' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG exclusion list verification is performed for all staff per federal requirements.',
      atRisk: 'Exclusion list verification may be inconsistent. Implement monthly checks for all employees.',
      nonCompliant: 'OIG exclusion list verification is not being performed. This creates significant compliance risk.',
      unknown: 'Verify exclusion list checking procedures are in place.',
    },
    stateNotes: [
      'OIG exclusion list check required at hire and monthly per federal guidelines',
      'Check at exclusions.oig.hhs.gov for all employees',
      'Medicaid exclusion list must also be verified',
      'Employing excluded individuals can result in civil monetary penalties',
      'Document all exclusion list checks in personnel files',
    ],
  },

  // ============================================
  // SECTION C: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'NY-C1',
    itemId: 'C1',
    stateCode: 'NY',
    title: 'HHA Initial Training (75 Hours)',
    description: 'HHA training requirements per 10 NYCRR 766.11',
    severity: 'high',
    regulationCode: '10 NYCRR 766.11',
    regulationName: 'HHA Training',
    agencyName: 'New York State Department of Health',
    agencyAbbr: 'NYS DOH',
    agencyPhone: '1-800-628-5972',
    agencyUrl: 'https://www.health.ny.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL Home Health Aides complete the following training BEFORE patient contact?',
        inputType: 'checklist',
        options: [
          { value: 'classroom', label: 'Classroom instruction from DOH/NYSED-approved program' },
          { value: 'clinical', label: 'Supervised practical training (minimum 16 hours, 50% in patient care setting)' },
          { value: 'total_75', label: 'Minimum 75 total hours of training' },
          { value: 'competency', label: 'Competency evaluation' },
          { value: 'documentation', label: 'Training documentation in personnel file' },
        ],
        required: true,
        helpText: 'Per 10 NYCRR 700.2(b)(9) and NYS DOH training requirements',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HHA training program meets New York requirements (75 hours before patient contact).',
      atRisk: 'HHA training may be incomplete. 75 hours required with 16 hours supervised practical training.',
      nonCompliant: 'HHAs are providing services without required training. Immediately complete training for any HHA who started without it.',
      unknown: 'Review HHA training program against NYS DOH requirements.',
    },
    stateNotes: [
      'Minimum 75 total hours of training required per 10 NYCRR 700.2(b)(9)',
      'At least 16 hours must be supervised practical training (50% in patient care setting)',
      'Training must be from DOH or NYSED-approved program',
      'Competency evaluation required before patient contact',
      'Training documentation must be maintained in personnel file',
    ],
  },

  {
    id: 'NY-C2',
    itemId: 'C2',
    stateCode: 'NY',
    title: 'Annual In-Service Training (12 Hours)',
    description: 'Annual in-service training requirements',
    severity: 'high',
    regulationCode: '10 NYCRR 766.11',
    regulationName: 'In-Service',
    agencyName: 'New York State Department of Health',
    agencyAbbr: 'NYS DOH',
    agencyPhone: '1-800-628-5972',
    agencyUrl: 'https://www.health.ny.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL Home Health Aides complete 12 hours of annual in-service training?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all HHAs complete 12+ hours annually' },
          { value: 'most', label: 'Most complete required hours, some are behind' },
          { value: 'no', label: 'No structured annual training program' },
        ],
        required: true,
        helpText: 'Annual in-service is a condition of employment per DAL DHCBS 22-07',
      },
      {
        id: 'q2',
        question: 'Which required topics does your annual training cover? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'infection', label: 'Infection control' },
          { value: 'safety', label: 'Patient safety' },
          { value: 'abuse', label: 'Abuse/neglect recognition and reporting' },
          { value: 'emergency', label: 'Emergency procedures' },
          { value: 'communication', label: 'Communication skills' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'countAtLeast', questionId: 'q2', value: 2 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Annual training program meets NYS requirements (12 hours with required topics).',
      atRisk: 'Some HHAs may be behind on annual training. 12 hours required annually.',
      nonCompliant: 'No structured annual training program exists. Implement a 12-hour annual training program immediately.',
      unknown: 'Review annual training program against NYS requirements.',
    },
    stateNotes: [
      'Annual in-service training is a condition of employment per DAL DHCBS 22-07',
      '12 hours minimum required annually per 10 NYCRR 766.11',
      'Must include infection control, patient safety, and emergency procedures',
      'Abuse/neglect recognition and reporting training required',
      'Document all in-service training in personnel files',
    ],
  },

  {
    id: 'NY-C3',
    itemId: 'C3',
    stateCode: 'NY',
    title: 'Competency Evaluation',
    description: 'Competency evaluation requirements per 10 NYCRR 766.11',
    severity: 'high',
    regulationCode: '10 NYCRR 766.11',
    regulationName: 'Competency',
    agencyName: 'New York State Department of Health',
    agencyAbbr: 'NYS DOH',
    agencyPhone: '1-800-628-5972',
    agencyUrl: 'https://www.health.ny.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your HHAs have documented competency evaluations on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all HHAs have completed competency evaluations' },
          { value: 'most', label: 'Most HHAs have them' },
          { value: 'no', label: 'No / not tracking this' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are competency evaluations performed by an RN or LPN under RN supervision?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / not always' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All HHAs have competency evaluations per 10 NYCRR 766.11.',
      atRisk: 'Some competency evaluations may be missing. Review all personnel files.',
      nonCompliant: 'HHAs are working without documented competency evaluations. This violates 10 NYCRR 766.11.',
      unknown: 'Verify all HHAs have competency evaluations per 10 NYCRR 766.11.',
    },
    stateNotes: [
      'Competency evaluation required before patient contact per 10 NYCRR 766.11',
      'Must be performed by RN or LPN under RN supervision',
      'Evaluation must include skills demonstration',
      'Document competency evaluation results in personnel file',
    ],
  },

  // ============================================
  // SECTION D: PATIENT CARE
  // ============================================
  {
    id: 'NY-D1',
    itemId: 'D1',
    stateCode: 'NY',
    title: 'Plan of Care (10 NYCRR 766.3)',
    description: 'Written plan of care requirements',
    severity: 'high',
    regulationCode: '10 NYCRR 766.3',
    regulationName: 'Plan of Care',
    agencyName: 'New York State Department of Health',
    agencyAbbr: 'NYS DOH',
    agencyPhone: '1-800-628-5972',
    agencyUrl: 'https://www.health.ny.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written plan of care for EVERY patient before services begin per 10 NYCRR 766.3?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your plans of care include all required elements?',
        inputType: 'checklist',
        options: [
          { value: 'diagnosis', label: 'Patient diagnosis and medical history' },
          { value: 'services', label: 'Specific services to be provided' },
          { value: 'frequency', label: 'Frequency and duration of services' },
          { value: 'goals', label: 'Patient-specific goals' },
          { value: 'physician', label: 'Physician orders (as applicable per 766.4)' },
          { value: 'review', label: 'Plan review schedule' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plans of care are completed before services begin per 10 NYCRR 766.3.',
      atRisk: 'Plans of care may be incomplete or created after services begin.',
      nonCompliant: 'Services are being provided without written plans of care. This violates 10 NYCRR 766.3.',
      unknown: 'Review plan of care process against 10 NYCRR 766.3 requirements.',
    },
    stateNotes: [
      'Written plan of care required for every patient per 10 NYCRR 766.3',
      'Plan must be established before services begin',
      'Must include diagnosis, services, frequency, duration, and goals',
      'Physician orders required as applicable per 10 NYCRR 766.4',
      'Plan must be reviewed and updated regularly',
    ],
  },

  {
    id: 'NY-D2',
    itemId: 'D2',
    stateCode: 'NY',
    title: 'Medical Orders (10 NYCRR 766.4)',
    description: 'Medical orders from authorized practitioner requirements',
    severity: 'high',
    regulationCode: '10 NYCRR 766.4',
    regulationName: 'Orders',
    agencyName: 'New York State Department of Health',
    agencyAbbr: 'NYS DOH',
    agencyPhone: '1-800-628-5972',
    agencyUrl: 'https://www.health.ny.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have documented medical orders from authorized practitioners for patients who require them per 10 NYCRR 766.4(a)?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all applicable patients have orders' },
          { value: 'most', label: 'Most have orders' },
          { value: 'no', label: 'No / inconsistent' },
        ],
        required: true,
        helpText: 'Required for patients being treated for health problems, with health care needs, or receiving HHA services',
      },
      {
        id: 'q2',
        question: 'Are medical orders reviewed and revised at least every 6 months per 10 NYCRR 766.4(c)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
      {
        id: 'q3',
        question: 'Are medical orders authenticated by an authorized practitioner within 12 months per 10 NYCRR 766.4(d)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Medical orders meet 10 NYCRR 766.4 requirements.',
      atRisk: 'Medical orders may not be current for all patients. Review every 6 months per 766.4(c).',
      nonCompliant: 'Medical orders are not being properly maintained. This violates 10 NYCRR 766.4.',
      unknown: 'Review medical order requirements per 10 NYCRR 766.4.',
    },
    stateNotes: [
      'Medical orders required for patients with health care needs per 10 NYCRR 766.4(a)',
      'Orders must be reviewed at least every 6 months per 10 NYCRR 766.4(c)',
      'Orders must be authenticated by authorized practitioner within 12 months per 10 NYCRR 766.4(d)',
      'Orders must be in writing or verbal orders must be signed promptly',
    ],
  },

  {
    id: 'NY-D3',
    itemId: 'D3',
    stateCode: 'NY',
    title: 'Clinical Supervision (10 NYCRR 766.5)',
    description: 'Clinical supervision requirements for patient care',
    severity: 'high',
    regulationCode: '10 NYCRR 766.5',
    regulationName: 'Supervision',
    agencyName: 'New York State Department of Health',
    agencyAbbr: 'NYS DOH',
    agencyPhone: '1-800-628-5972',
    agencyUrl: 'https://www.health.ny.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide clinical supervision of HHAs as required per 10 NYCRR 766.5?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, per regulatory schedule' },
          { value: 'partial', label: 'Sometimes, not consistently' },
          { value: 'no', label: 'No clinical supervision program' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How often does clinical supervision occur?',
        inputType: 'select',
        options: [
          { value: 'every_60', label: 'At least every 60 days' },
          { value: 'every_90', label: 'Every 90 days' },
          { value: 'less', label: 'Less frequently' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'every_60', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'every_90', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'less', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical supervision schedule meets 10 NYCRR 766.5 requirements.',
      atRisk: 'Clinical supervision may not meet required frequency. Review schedule.',
      nonCompliant: 'No clinical supervision program exists. This violates 10 NYCRR 766.5.',
      unknown: 'Review clinical supervision requirements per 10 NYCRR 766.5.',
    },
    stateNotes: [
      'Clinical supervision of HHAs required per 10 NYCRR 766.5',
      'Supervision must occur at least every 60 days',
      'RN must observe aide performance and patient status',
      'Document supervision visits in patient care record',
      'Address any competency or care quality issues immediately',
    ],
  },

  {
    id: 'NY-D4',
    itemId: 'D4',
    stateCode: 'NY',
    title: 'Patient Care Record (10 NYCRR 766.6)',
    description: 'Patient care record requirements',
    severity: 'high',
    regulationCode: '10 NYCRR 766.6',
    regulationName: 'Records',
    agencyName: 'New York State Department of Health',
    agencyAbbr: 'NYS DOH',
    agencyPhone: '1-800-628-5972',
    agencyUrl: 'https://www.health.ny.gov',
    questions: [
      {
        id: 'q1',
        question: 'Are all patient visits documented in writing per 10 NYCRR 766.6?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all visits are documented' },
          { value: 'most', label: 'Most visits are documented' },
          { value: 'no', label: 'Documentation is inconsistent' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do visit notes include all required elements?',
        inputType: 'checklist',
        options: [
          { value: 'date_time', label: 'Date and time of visit' },
          { value: 'services', label: 'Services provided' },
          { value: 'patient_status', label: 'Patient status and observations' },
          { value: 'signature', label: 'Aide signature' },
          { value: 'duration', label: 'Duration of visit' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient care records are complete per 10 NYCRR 766.6.',
      atRisk: 'Visit documentation may be incomplete. Ensure all visits are properly documented.',
      nonCompliant: 'Patient care records are inconsistent. This violates 10 NYCRR 766.6.',
      unknown: 'Review patient care record practices per 10 NYCRR 766.6.',
    },
    stateNotes: [
      'Patient care record required for every patient per 10 NYCRR 766.6',
      'Visit notes must include date, time, services provided, and observations',
      'Aide must sign all visit documentation',
      'Records must be maintained for 6 years per 10 NYCRR 766.12',
      'Documentation must be timely - ideally completed same day as visit',
    ],
  },

  // ============================================
  // SECTION E: PATIENT RIGHTS
  // ============================================
  {
    id: 'NY-E1',
    itemId: 'E1',
    stateCode: 'NY',
    title: 'Patient Rights (10 NYCRR 766.1)',
    description: 'Patient rights requirements',
    severity: 'high',
    regulationCode: '10 NYCRR 766.1',
    regulationName: 'Rights',
    agencyName: 'New York State Department of Health',
    agencyAbbr: 'NYS DOH',
    agencyPhone: '1-800-628-5972',
    agencyUrl: 'https://www.health.ny.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide written patient rights to all patients per 10 NYCRR 766.1?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all patients receive written rights' },
          { value: 'most', label: 'Most patients receive them' },
          { value: 'no', label: 'No formal patient rights document' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you have signed acknowledgment of receipt in patient records?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient rights are provided and acknowledged per 10 NYCRR 766.1.',
      atRisk: 'Patient rights distribution may be inconsistent. Ensure all patients receive and acknowledge.',
      nonCompliant: 'Patient rights are not being provided. This violates 10 NYCRR 766.1.',
      unknown: 'Review patient rights documentation practices per 10 NYCRR 766.1.',
    },
    stateNotes: [
      'Written patient rights must be provided to all patients per 10 NYCRR 766.1',
      'Rights must be provided in language patient can understand',
      'Obtain signed acknowledgment of receipt for patient record',
      'Patient rights include right to refuse treatment and file complaints',
      'Must include DOH complaint hotline: 1-800-628-5972',
    ],
  },

  // ============================================
  // SECTION F: HEALTH & SAFETY
  // ============================================
  {
    id: 'NY-F1',
    itemId: 'F1',
    stateCode: 'NY',
    title: 'TB Screening',
    description: 'TB screening requirements for patient care staff',
    severity: 'high',
    regulationCode: '10 NYCRR 766.11',
    regulationName: 'TB Screening',
    agencyName: 'New York State Department of Health',
    agencyAbbr: 'NYS DOH',
    agencyPhone: '1-800-628-5972',
    agencyUrl: 'https://www.health.ny.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your patient care staff have current TB screening on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have current TB screening' },
          { value: 'most', label: 'Most do, some may be expired' },
          { value: 'no', label: 'No / Not tracking this' },
        ],
        required: true,
        helpText: 'TB screening must be completed BEFORE patient contact',
      },
      {
        id: 'q2',
        question: 'How do you track TB screening expiration dates?',
        inputType: 'select',
        options: [
          { value: 'system', label: 'Automated system with reminders' },
          { value: 'manual', label: 'Manual tracking (spreadsheet, calendar)' },
          { value: 'none', label: 'No active tracking system' },
        ],
      },
    ],
    scoringRules: [
      // 1. NON_COMPLIANT - Check first: No TB screening tracking
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // 2. COMPLIANT - All staff have current TB screening with active tracking
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'includes', questionId: 'q2', value: ['system', 'manual'] },
      ], result: 'COMPLIANT' },
      // 3. AT_RISK - Some expired or no tracking system
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'none' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'TB screening documentation is current for all staff per 10 NYCRR 766.11.',
      atRisk: 'Some TB screenings may be expired. Review all staff files.',
      nonCompliant: 'Staff are working without valid TB screening. This violates 10 NYCRR 766.11.',
      unknown: 'Verify TB screening status for all staff per 10 NYCRR 766.11.',
    },
    stateNotes: [
      'TB screening required for all patient care staff per 10 NYCRR 766.11',
      'Screening must be completed before patient contact',
      'Two-step TST or IGRA test acceptable',
      'Track expiration dates and renew as required by agency policy',
      'Positive results require chest X-ray and medical evaluation',
    ],
  },

  {
    id: 'NY-F2',
    itemId: 'F2',
    stateCode: 'NY',
    title: 'Health Assessment',
    description: 'Health assessment requirements for direct care staff',
    severity: 'medium',
    regulationCode: '10 NYCRR 766.11',
    regulationName: 'Health Exams',
    agencyName: 'New York State Department of Health',
    agencyAbbr: 'NYS DOH',
    agencyPhone: '1-800-628-5972',
    agencyUrl: 'https://www.health.ny.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL direct care employees have health assessments on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all have health assessments' },
          { value: 'most', label: 'Most have them' },
          { value: 'no', label: 'No / not tracking' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Health assessments are on file for all direct care staff per 10 NYCRR 766.11.',
      atRisk: 'Some staff may be missing health assessments. Review all personnel files.',
      nonCompliant: 'Health assessments are not being maintained. This violates 10 NYCRR 766.11.',
      unknown: 'Verify health assessment documentation for all staff.',
    },
    stateNotes: [
      'Health assessment required for all direct care employees per 10 NYCRR 766.11',
      'Must document that employee is physically able to perform job duties',
      'Assessment should be completed by licensed healthcare provider',
      'Keep health assessment documentation in personnel file',
    ],
  },
];

export default NEWYORK_QUESTIONS;